import { useEffect, useState } from 'react';
import { Suspense } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon, Skeleton, ThemeToggle, useThemeSync, type IconName } from '@marcianus/design/components';
import { UserButton } from '@clerk/clerk-react';
import { getDashboard, getMyProfile, setThemePreference } from '@/api/admin';
import { ImpersonationBar } from './ImpersonationBar';
import { HexisFab } from './HexisFab';
import { useTranslation } from 'react-i18next';
import { getFormatLocale } from '@marcianus/shared/format';
import { LanguageSwitcher } from '@/i18n/LanguageSwitcher';
import { normalizeLocale, setLocale } from '@/i18n';

// Sidebar em SECÇÕES — a consola lê-se como a operação se organiza:
// primeiro o dia-a-dia (Operação), depois análise, publicação, relação com o
// cliente e, no fim, governança (fiscal/auditoria/permissões).
// Os rótulos são CHAVES do catálogo (hq:nav.*) — os caminhos e ícones são identificadores.
type NavKey = 'overview' | 'users' | 'orgs' | 'events' | 'bi' | 'studio' | 'marketplace' | 'support' | 'fiscal' | 'audit' | 'permissions';
type SectionKey = 'operation' | 'intelligence' | 'publishing' | 'relation' | 'governance';
const SECTIONS: { title: SectionKey; items: { to: string; label: NavKey; icon: IconName }[] }[] = [
  {
    title: 'operation',
    items: [
      { to: '/', label: 'overview', icon: 'grid' },
      { to: '/utilizadores', label: 'users', icon: 'users' },
      { to: '/organizacoes', label: 'orgs', icon: 'briefcase' },
      { to: '/eventos', label: 'events', icon: 'calendar' },
    ],
  },
  { title: 'intelligence', items: [{ to: '/bi', label: 'bi', icon: 'chart' }] },
  {
    title: 'publishing',
    items: [
      { to: '/conteudos', label: 'studio', icon: 'edit' },
      { to: '/marketplace', label: 'marketplace', icon: 'shop' },
    ],
  },
  { title: 'relation', items: [{ to: '/suporte', label: 'support', icon: 'msgCircle' }] },
  {
    title: 'governance',
    items: [
      { to: '/fiscal', label: 'fiscal', icon: 'creditCard' },
      { to: '/auditoria', label: 'audit', icon: 'activity' },
      { to: '/permissoes', label: 'permissions', icon: 'shield' },
    ],
  },
];

// Relógio ao vivo (hh:mm:ss) — o "pulso" da operação, como no design.
function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return <span className="topbar-clock mono">{now.toLocaleTimeString(getFormatLocale())}</span>;
}

export function Shell() {
  const { t, i18n } = useTranslation('hq');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [q, setQ] = useState('');

  // O <title> era fixo («Marcianus HQ · Consola») em todas as 11 páginas: o
  // separador do browser mentia, o histórico ficava indistinguível e um leitor
  // de ecrã não anunciava a mudança de página (WCAG 2.4.2). O nome vem do
  // próprio menu — a SSOT que já nomeia cada rota.
  const rotaKey = SECTIONS.flatMap((s2) => s2.items)
    .find((i) => (i.to === '/' ? pathname === '/' : pathname.startsWith(i.to)))?.label;
  const nomeDaRota = rotaKey ? t(`nav.${rotaKey}`) : t('shell.console');
  const [anuncio, setAnuncio] = useState('');
  useEffect(() => {
    document.title = `${nomeDaRota} · Marcianus HQ`;
    setAnuncio(nomeDaRota);
  }, [pathname, nomeDaRota, i18n.language]);
  // Contagem de eventos ao vivo → badge "OPERAÇÃO LIVE" (dado real do dashboard).
  const { data: dash } = useQuery({ queryKey: ['dashboard'], queryFn: getDashboard, staleTime: 60_000 });
  const live = Number(dash?.liveEvents ?? 0) || 0;

  // O tema é da PESSOA, não do dispositivo: quem escolhe claro no telemóvel
  // encontra a consola clara ao sentar-se à secretária (THEME-SYSTEM §5).
  const { data: profile } = useQuery({ queryKey: ['me', 'profile'], queryFn: getMyProfile, staleTime: 5 * 60_000, retry: false });
  useThemeSync({
    persist: (t) => { void setThemePreference(t).catch(() => {}); },
    serverTheme: profile?.preferences?.theme,
  });

  // O idioma guardado no perfil ganha ao do dispositivo (precedência em shared/i18n-core).
  useEffect(() => {
    const l = normalizeLocale(profile?.preferences?.locale);
    if (l && l !== i18n.language) void setLocale(l);
  }, [profile?.preferences?.locale, i18n.language]);

  const search = (e: React.FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    // A pesquisa principal é de pessoas (como no design) → abre Utilizadores.
    navigate(term ? `/utilizadores?q=${encodeURIComponent(term)}` : '/utilizadores');
  };

  return (
    <div className="app-grid">
      {/* PRIMEIRO elemento do documento — a ordem do DOM é a ordem do Tab, e um
          «saltar a navegação» que só aparece depois da navegação não salta nada
          (WCAG 2.4.1). Sem ele, um utilizador de teclado percorre os 12 itens da
          barra lateral e a barra do topo antes do conteúdo, em TODAS as páginas.
          O estilo já existia no MDS (`.skip-link`) — faltava alguém usá-lo. */}
      <a className="skip-link" href="#conteudo">{t('shell.skip')}</a>
      {/* Só para quem ouve: nomeia a página a cada navegação (WCAG 2.4.2). */}
      <p className="sr-only" role="status" aria-live="polite">{anuncio}</p>
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="hdr-brand hdr-brand--lg"><img src="/symbol.png" alt="Marcianus" /></span>
          <div>
            <div className="brand-name">Marcianus HQ</div>
            <div className="brand-sub">{t('shell.brandSub')}</div>
          </div>
        </div>

        {/* Estado da operação — verde a pulsar quando há eventos ao vivo. */}
        <div className={`op-live${live > 0 ? ' on' : ''}`}>
          <span className="live-dot" style={{ background: live > 0 ? 'var(--ink-success)' : 'var(--fg-40)' }} />
          <span>{live > 0 ? t('shell.opLive') : t('shell.noLive')}</span>
          {live > 0 && <span className="op-live-count">{t('shell.liveCount', { count: live })}</span>}
        </div>

        <nav className="nav">
          {SECTIONS.map((s) => (
            <div key={s.title}>
              <div className="nav-section">{t(`nav.${s.title}`)}</div>
              {s.items.map((n) => (
                <NavLink key={n.to} to={n.to} end className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                  <span className="nav-ico"><Icon name={n.icon} size={18} /></span>{t(`nav.${n.label}`)}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          <UserButton />
          <span className="muted small">{t('shell.teamSession')}</span>
        </div>
      </aside>

      <main className="content" id="conteudo" tabIndex={-1}>
        {/* Top bar — pesquisa global + relógio + criar evento (como no design). */}
        <header className="topbar">
          <form className="search-bar topbar-search" onSubmit={search}>
            <span className="topbar-search-ico"><Icon name="search" size={18} /></span>
            <input value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('shell.searchLabel')} placeholder={t('shell.searchPh')} />
            <kbd className="topbar-kbd">⌘K</kbd>
          </form>
          <div className="topbar-right">
            <LanguageSwitcher />
            <ThemeToggle />
            <LiveClock />
            <button className="btn btn-purple topbar-cta" onClick={() => navigate('/eventos')}>{t('shell.createEvent')}</button>
          </div>
        </header>

        {/* Se estamos a operar como outra pessoa, isso vem ANTES de tudo. */}
        <ImpersonationBar />
        {/* As páginas chegam em pedaços separados (lazy). O esqueleto ocupa o
            lugar do conteúdo em vez de deixar a área em branco — a navegação e
            o cabeçalho nunca desaparecem entre ecrãs. */}
        <Suspense fallback={<div style={{ padding: 16, display: 'grid', gap: 12 }}>
          <Skeleton height={40} borderRadius={10} />
          <Skeleton height={180} borderRadius={12} />
          <Skeleton height={180} borderRadius={12} />
        </div>}>
          <Outlet />
        </Suspense>
      </main>

      {/* O MESMO copiloto do www — um cérebro para todos os papéis. */}
      <HexisFab />
    </div>
  );
}
